import { Controller } from "@hotwired/stimulus";

let FancyboxLib = null;

async function loadFancybox() {
  if (FancyboxLib) return FancyboxLib;

  const module = await import("@fancyapps/ui");
  FancyboxLib = module.Fancybox || module.default?.Fancybox || module.default;

  return FancyboxLib;
}

export default class FancyboxController extends Controller {
  async open(event) {
    event.preventDefault();
    event.stopPropagation();

    const trigger = event.currentTarget;
    const file = trigger.fancyboxFile;
    const isBlob = file instanceof Blob;
    const src = isBlob ? URL.createObjectURL(file) : trigger.dataset.src;

    if (!src) return;

    const Fancybox = await loadFancybox();
    Fancybox.show([{ src, type: "image" }], {
      Hash: false,
      zoomEffect: false,
      backdropClick: false,
      fadeEffect: false,
      showClass: false,
      hideClass: false,
      Carousel: {
        Toolbar: {
          display: {
            left: [],
            middle: [],
            right: ["zoomIn", "zoomOut", "fullscreen", "close"],
          },
        },
        Zoomable: {
          Panzoom: {
            maxScale: 4,
            panMode: "drag",
            mouseMoveFactor: 0,
            clickAction: false,
            singleClickAction: this.zoomClickAction.bind(this),
            dblClickAction: this.zoomClickAction.bind(this),
            on: this.zoomCursorEvents(),
          },
        },
      },
      on: {
        ready: (fb) => {
          this.suspendFeedSlider();
          this.watchFullscreenExit(fb);
        },
        shouldClose: (fb) => this.hideFullscreenViewer(fb),
        destroy: (fb) => {
          this.stopWatchingFullscreen(fb);
          this.resumeFeedSlider();
          if (isBlob) URL.revokeObjectURL(src);
        }
      }
    });
  }

  connect() {
    this.loadFancyboxAndInitialize();
  }

  async loadFancyboxAndInitialize() {
    this.Fancybox = await loadFancybox();

    this.Fancybox.unbind(this.element, "[data-fancybox]");

    this.Fancybox.bind(this.element, "[data-fancybox]", {
      Hash: false,
      zoomEffect: false,
      backdropClick: false,
      fadeEffect: false,
      showClass: false,
      hideClass: false,
      dragToClose: false,

      Carousel: {
        gestures: false,

        Video: {
          autoplay: true
        },

        Toolbar: {
          display: {
            left: [],
            middle: [],
            right: ["zoomIn", "zoomOut", "fullscreen", "close"]
          }
        },

        Zoomable: {
          Panzoom: {
            maxScale: 4,
            panMode: "drag",
            mouseMoveFactor: 0,
            clickAction: false,
            singleClickAction: this.zoomClickAction.bind(this),
            dblClickAction: this.zoomClickAction.bind(this),
            on: this.zoomCursorEvents()
          }
        }
      },

      on: {
        ready: (fb) => {
          this.suspendFeedSlider();
          this.watchFullscreenExit(fb);
        },

        shouldClose: (fb) => {
          this.hideFullscreenViewer(fb);
        },

        "Carousel.contentLoading": (fb, carousel, slide) => {
          this.showImagePlaceholder(slide);
        },

        "Carousel.contentReady": (fb, carousel, slide) => {
          this.clearImagePlaceholder(slide);

          const trigger = slide.triggerEl;
          const feedVideoId = trigger?.dataset.feedVideoId;

          const feedVideo =
            trigger?.querySelector("video") ||
            (feedVideoId && document.getElementById(feedVideoId)?.querySelector("video")) ||
            trigger?.closest("[data-post]")?.querySelector("video");

          const modalVideo =
            slide.el?.querySelector(".f-html5video video") ||
            slide.el?.querySelector("video");

          if (!feedVideo || !modalVideo) return;

          slide.feedVideo = feedVideo;
          slide.modalVideo = modalVideo;
          slide.originalMuted = feedVideo.muted;
          slide.originalVolume = feedVideo.volume;
          slide.originalPlaybackRate = feedVideo.playbackRate;
          slide.wasPlaying = !feedVideo.paused;

          feedVideo.muted = true;

          modalVideo.currentTime = feedVideo.currentTime;
          modalVideo.volume = feedVideo.volume;
          modalVideo.playbackRate = feedVideo.playbackRate;
          modalVideo.muted = slide.originalMuted;

          if (slide.wasPlaying) {
            modalVideo.play().catch(() => { });
          }
        },

        close: (fb) => {
          const slide = fb.getSlide();

          if (!slide?.feedVideo || !slide?.modalVideo) return;

          const { feedVideo, modalVideo } = slide;

          feedVideo.currentTime = modalVideo.currentTime;
          feedVideo.volume = modalVideo.volume;
          feedVideo.playbackRate = modalVideo.playbackRate;
          feedVideo.muted = slide.originalMuted;

          if (slide.wasPlaying) {
            feedVideo.play().catch(() => { });
          }

          modalVideo.pause();
        },

        destroy: (fb) => {
          this.stopWatchingFullscreen(fb);
          this.resumeFeedSlider();
        }
      }
    });
  }

  suspendFeedSlider() {
    this.feedSliderController = this.element
      .closest("[data-controller~=\"feed-video\"]")
      ?.feedVideoController;

    this.feedSliderController?.suspendSliderResponsiveUpdates();
  }

  resumeFeedSlider() {
    this.feedSliderController?.resumeSliderResponsiveUpdates();
    this.feedSliderController = null;
  }

  watchFullscreenExit(fancybox) {
    this.fullscreenWatchers ||= new WeakMap();
    if (this.fullscreenWatchers.has(fancybox)) return;

    let wasFullscreen = false;
    const fullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;

      if (fullscreenElement) {
        wasFullscreen = fullscreenElement === fancybox.getContainer();
        return;
      }

      if (!wasFullscreen) return;

      wasFullscreen = false;
    };

    document.addEventListener("fullscreenchange", fullscreenChange);
    document.addEventListener("webkitfullscreenchange", fullscreenChange);
    this.fullscreenWatchers.set(fancybox, { fullscreenChange });
  }

  stopWatchingFullscreen(fancybox) {
    const watcher = this.fullscreenWatchers?.get(fancybox);
    if (!watcher) return;

    document.removeEventListener("fullscreenchange", watcher.fullscreenChange);
    document.removeEventListener("webkitfullscreenchange", watcher.fullscreenChange);
    this.fullscreenWatchers.delete(fancybox);
  }

  hideFullscreenViewer(fancybox) {
    const fullscreen = fancybox.getPlugins()?.Fullscreen;

    if (!fullscreen?.inFullscreen()) return;

    this.hideViewerImmediately(fancybox);
  }

  hideViewerImmediately(fancybox) {
    const container = fancybox.getContainer();
    if (!container) return;

    container.style.transition = "none";
    container.style.animation = "none";
    container.style.visibility = "hidden";
  }

  showImagePlaceholder(slide) {
    const triggerImage = slide.triggerEl?.querySelector("img");
    const thumbnailSrc = triggerImage?.currentSrc || triggerImage?.src;

    if (!slide.el || !thumbnailSrc || slide.triggerEl?.querySelector("video")) return;

    const imageWidth = Number(slide.width) ||
      Number(slide.triggerEl.dataset.width) ||
      triggerImage.naturalWidth;
    const imageHeight = Number(slide.height) ||
      Number(slide.triggerEl.dataset.height) ||
      triggerImage.naturalHeight;
    const fancyboxViewport = slide.el.closest(".fancybox__viewport");
    const frameWidth = slide.el.clientWidth ||
      fancyboxViewport?.clientWidth ||
      globalThis.innerWidth;
    const frameHeight = slide.el.clientHeight ||
      fancyboxViewport?.clientHeight ||
      globalThis.innerHeight;
    const imageExceedsSlide = imageWidth > frameWidth ||
      imageHeight > frameHeight;
    const scale = 1;

    slide.el.querySelector(".f-spinner")?.remove();
    slide.el.style.backgroundImage = `url("${thumbnailSrc}")`;
    slide.el.style.backgroundPosition = "center";
    slide.el.style.backgroundRepeat = "no-repeat";
    if (imageExceedsSlide) {
      slide.el.style.backgroundSize = "contain";
    } else if (imageWidth && imageHeight) {
      slide.el.style.backgroundSize =
        `${Math.round(imageWidth * scale)}px ${Math.round(imageHeight * scale)}px`;
    } else {
      slide.el.style.backgroundSize = "auto";
    }
    slide.hasImagePlaceholder = true;
  }

  clearImagePlaceholder(slide) {
    if (!slide.hasImagePlaceholder || !slide.el) return;

    slide.el.style.removeProperty("background-image");
    slide.el.style.removeProperty("background-position");
    slide.el.style.removeProperty("background-repeat");
    slide.el.style.removeProperty("background-size");
    slide.hasImagePlaceholder = false;
  }

  zoomCursorEvents() {
    const update = (panzoom) => this.updateZoomCursor(panzoom);
    return { ready: update, animationStart: update, animationEnd: update };
  }

  updateZoomCursor(panzoom) {
    const wrapper = panzoom.getWrapper();
    if (!wrapper) return;

    // Drag classes change on pointer press/release. Keep the zoom cursor tied
    // to the target scale instead so it changes only once per zoom action.
    const scale = panzoom.getTransform(true).scale;
    wrapper.style.cursor = scale > panzoom.getScale("base") + 0.01
      ? "zoom-out"
      : "zoom-in";
  }

  zoomClickAction(panzoom) {
    // The displayed scale is between zoom levels while the tween is running.
    // Ignore extra clicks until it settles instead of stacking zoom-out actions.
    if (panzoom.getTween()?.isRunning()) return;

    const currentScale = panzoom.getScale();
    const baseScale = panzoom.getScale("base");
    if (currentScale <= baseScale + 0.01) {
      return "zoomIn";
    }

    return "zoomOut";
  }

  disconnect() {
    this.resumeFeedSlider();
    this.Fancybox?.unbind(this.element, "[data-fancybox]");
  }
}
